import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { subMonths, addMonths, startOfMonth, endOfMonth } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Plus, ArrowUpDown, ChevronUp, ChevronDown, CalendarDays, Filter, X, Layers, BookOpen, Trash2, MoreHorizontal, Archive } from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { adminApi, type BatchSummary, type OrgSummary } from '@/lib/api/admin';
import { formatDate } from '@/lib/date';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { CreateBatchModal } from '@/features/AdminBatches/CreateBatchModal';
import { ManageTrainersDrawer } from '@/features/AdminBatches/ManageTrainersDrawer';
import { CloneBatchModal } from '@/features/AdminBatches/CloneBatchModal';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from '@/components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

function fmtDate(d: Date) {
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function toLocalDateStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type TabKey = 'active' | 'upcoming' | 'ended' | 'archived';
type SortCol = 'name' | 'org' | 'period';
type SortDir = 'asc' | 'desc';

export const Route = createFileRoute('/admin/batches/')({
  validateSearch: (search: Record<string, unknown>) => ({
    tab: (search.tab as TabKey) || 'active',
    org: (search.org as string) || undefined,
  }),
  component: AdminBatchesPage,
});

function statusOf(b: BatchSummary): 'active' | 'upcoming' | 'ended' | 'archived' | 'draft' {
  if (b.archived_at) return 'archived';
  const today = new Date().toISOString().slice(0, 10);
  if (!b.start_date) return 'draft';
  if (b.end_date && b.end_date < today) return 'ended';
  if (b.start_date > today) return 'upcoming';
  return 'active';
}

const AVATAR_COLORS = [
  'bg-emerald-600', 'bg-blue-600', 'bg-violet-600', 'bg-amber-600',
  'bg-rose-600', 'bg-teal-600', 'bg-indigo-600', 'bg-fuchsia-600',
];
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}
function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function TrainerAvatars({ trainers, onManage }: { trainers: BatchSummary['trainers']; onManage: () => void }) {
  if (trainers.length === 0) {
    return (
      <button onClick={onManage} className="text-sm italic text-muted-foreground hover:text-foreground transition-colors">
        + Add
      </button>
    );
  }
  const shown = trainers.slice(0, 4);
  const extra = trainers.length - shown.length;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onManage}
          aria-label={`${trainers.length} trainer${trainers.length === 1 ? '' : 's'} — manage`}
          className="flex items-center -space-x-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {shown.map((t) => (
            <span
              key={t.user_id}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-semibold text-white ring-2 ring-card',
                colorFor(t.user_id),
              )}
            >
              {initials(t.username)}
            </span>
          ))}
          {extra > 0 && (
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground ring-2 ring-card">
              +{extra}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">
        <div className="space-y-0.5">
          {trainers.map((t) => (
            <div key={t.user_id} className="text-xs">{t.username}</div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function AdminBatchesPage() {
  const [batches, setBatches] = useState<BatchSummary[]>([]);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const navigate = useNavigate();
  const { tab: activeTab, org: orgFromUrl } = Route.useSearch();
  const [orgFilter, setOrgFilter] = useState(orgFromUrl ?? '');
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [manageBatch, setManageBatch] = useState<BatchSummary | null>(null);
  const [deletingBatch, setDeletingBatch] = useState<BatchSummary | null>(null);
  const [cloningBatch, setCloningBatch] = useState<BatchSummary | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletionPreview, setDeletionPreview] = useState<{ student_count: number; trainer_count: number } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sortCol, setSortCol] = useState<SortCol | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [activeRange, setActiveRange] = useState<DateRange | undefined>(undefined);
  const [endRange, setEndRange] = useState<DateRange | undefined>(undefined);

  const loadBatches = async (orgId?: string, tab?: TabKey) => {
    setLoading(true);
    try {
      const currentTab = tab ?? activeTab;
      setBatches(await adminApi.listBatches(orgId || undefined, currentTab === 'archived'));
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    adminApi.listOrgs().then(setOrgs).catch(() => toast.error('Failed to load organizations'));
    loadBatches(orgFromUrl || undefined, activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, orgFromUrl]);

  const handleArchive = async (b: BatchSummary) => {
    try {
      await adminApi.archiveBatch(b.id);
      setBatches((prev) => prev.filter((x) => x.id !== b.id));
      toast.success(`"${b.name}" archived`);
    } catch {
      toast.error('Failed to archive batch');
    }
  };

  const handleDelete = async () => {
    if (!deletingBatch) return;
    setDeleteLoading(true);
    try {
      await adminApi.deleteBatch(deletingBatch.id);
      setBatches((prev) => prev.filter((b) => b.id !== deletingBatch.id));
      toast.success(`"${deletingBatch.name}" deleted`);
      setDeletingBatch(null);
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Failed to delete batch');
    } finally {
      setDeleteLoading(false);
    }
  };

  function toggleSort(col: SortCol) {
    if (sortCol !== col) { setSortCol(col); setSortDir('asc'); }
    else if (sortDir === 'asc') setSortDir('desc');
    else setSortCol(null);
  }

  const visibleBatches = [...batches]
    .filter((b) => statusOf(b) === activeTab)
    .filter((b) => {
      const bStart = b.start_date ?? '';
      if (activeRange?.from && activeRange?.to) {
        const from = toLocalDateStr(activeRange.from);
        const to   = toLocalDateStr(activeRange.to);
        if (bStart < from || bStart > to) return false;
      }
      const bEnd = b.end_date ?? '';
      if (endRange?.from && endRange?.to) {
        const from = toLocalDateStr(endRange.from);
        const to   = toLocalDateStr(endRange.to);
        if (!bEnd || bEnd < from || bEnd > to) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (!sortCol) return 0;
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortCol === 'name')   return a.name.localeCompare(b.name) * dir;
      if (sortCol === 'org')    return a.org_name.localeCompare(b.org_name) * dir;
      if (sortCol === 'period') return (a.start_date ?? '').localeCompare(b.start_date ?? '') * dir;
      return 0;
    });

  function SortIcon({ col }: { col: SortCol }) {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 opacity-50" />;
    return sortDir === 'asc'
      ? <ChevronUp className="h-3 w-3 text-foreground" />
      : <ChevronDown className="h-3 w-3 text-foreground" />;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="space-y-6">
        {/* Header + Tabs */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 border-b border-border">
{(['active', 'upcoming', 'ended', 'archived'] as TabKey[]).map((tab) => (
              <button
                key={tab}
                onClick={() => navigate({ to: '/admin/batches', search: { tab, org: orgFilter || undefined } })}
                aria-current={activeTab === tab ? 'page' : undefined}
                className={cn(
                  'px-4 py-2.5 text-sm font-medium capitalize border-b-2 transition-colors',
                  activeTab === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {tab}
                <span
                  className={cn(
                    'ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                    activeTab === tab ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground',
                  )}
                >
                  {tab === 'archived' && activeTab !== 'archived' ? '—' : batches.filter((b) => statusOf(b) === tab).length}
                </span>
              </button>
            ))}
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-1.5">
            <Plus className="h-4 w-4" /> New Batch
          </Button>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card shadow-sm">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="w-[220px] text-[11px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
                  <span className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors" onClick={() => toggleSort('name')}>
                    Name <SortIcon col="name" />
                  </span>
                </TableHead>

                <TableHead className="w-[180px] text-[11px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
                  <span className="flex items-center gap-1">
                    <span className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors" onClick={() => toggleSort('org')}>
                      Organization <SortIcon col="org" />
                    </span>
                    <Popover modal>
                      <PopoverTrigger asChild>
                        {orgFilter ? (
                          <button className="ml-1 flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/20 transition-colors max-w-[160px] min-w-0">
                            <span className="truncate min-w-0">{orgs.find((o) => o.id === orgFilter)?.name ?? orgFilter}</span>
                            <span className="ml-0.5 shrink-0 hover:text-primary/60" onClick={(e) => { e.stopPropagation(); setOrgFilter(''); loadBatches(undefined, activeTab); }}>
                              <X className="h-2.5 w-2.5" />
                            </span>
                          </button>
                        ) : (
                          <button className="ml-0.5 rounded p-0.5 hover:bg-muted transition-colors text-muted-foreground">
                            <Filter className="h-3 w-3" />
                          </button>
                        )}
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-52 p-1">
                        {orgs.map((o) => (
                          <PopoverClose asChild key={o.id}>
                            <button
                              className={cn('w-full rounded px-2 py-1.5 text-left text-[12px] hover:bg-muted transition-colors', orgFilter === o.id && 'bg-primary/10 text-primary font-medium')}
                              onClick={() => { const next = orgFilter === o.id ? '' : o.id; setOrgFilter(next); loadBatches(next || undefined, activeTab); }}
                            >
                              {o.name}
                            </button>
                          </PopoverClose>
                        ))}
                      </PopoverContent>
                    </Popover>
                  </span>
                </TableHead>

                {/* Period — two independent filters: start date + end date */}
                <TableHead className="w-[210px] text-[11px] font-semibold uppercase tracking-wider text-muted-foreground select-none">
                  <span className="flex items-center gap-1">
                    <span className="flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors" onClick={() => toggleSort('period')}>
                      Period <SortIcon col="period" />
                    </span>

                    {/* Start date filter */}
                    <Popover modal>
                      <PopoverTrigger asChild>
                        {activeRange?.from && activeRange.to ? (
                          <button className="ml-0.5 flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/20 transition-colors">
                            ▶ {fmtDate(activeRange.from)}–{fmtDate(activeRange.to)}
                            <span className="ml-0.5 hover:text-primary/60" onClick={(e) => { e.stopPropagation(); setActiveRange(undefined); }}>
                              <X className="h-2.5 w-2.5" />
                            </span>
                          </button>
                        ) : (
                          <button className="ml-0.5 rounded p-0.5 hover:bg-muted transition-colors text-muted-foreground" title="Filter by start date">
                            <CalendarDays className="h-3 w-3" />
                          </button>
                        )}
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-auto p-0">
                        <div className="p-2 border-b flex items-center justify-between">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Start date</p>
                          {activeRange?.from && (
                            <button className="text-[10px] text-muted-foreground hover:text-foreground transition-colors" onClick={() => setActiveRange(undefined)}>Clear</button>
                          )}
                        </div>
                        <div className="flex">
                          <Calendar mode="range" selected={activeRange} onSelect={setActiveRange} numberOfMonths={1} />
                          <div className="flex flex-col gap-0.5 border-l border-border p-2 w-[140px] shrink-0">
                            <p className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Quick pick</p>
                            {([
                              { label: 'Last 1 month',  range: () => { const t = new Date(); return { from: subMonths(t, 1), to: t }; } },
                              { label: 'Last 2 months', range: () => { const t = new Date(); return { from: subMonths(t, 2), to: t }; } },
                              { label: 'Last 3 months', range: () => { const t = new Date(); return { from: subMonths(t, 3), to: t }; } },
                              { label: 'This month',    range: () => { const t = new Date(); return { from: startOfMonth(t), to: endOfMonth(t) }; } },
                              { label: 'Next 1 month',  range: () => { const t = new Date(); return { from: t, to: addMonths(t, 1) }; } },
                              { label: 'Next 2 months', range: () => { const t = new Date(); return { from: t, to: addMonths(t, 2) }; } },
                            ] as const).map((p) => (
                              <button key={p.label} type="button" onClick={() => setActiveRange(p.range())}
                                className="rounded-md px-2.5 py-1.5 text-left text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                                {p.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="p-2 border-t flex justify-end">
                          <PopoverClose asChild><Button size="sm" variant="outline" className="h-7 text-xs">Done</Button></PopoverClose>
                        </div>
                      </PopoverContent>
                    </Popover>

                    {/* End date filter */}
                    <Popover modal>
                      <PopoverTrigger asChild>
                        {endRange?.from && endRange.to ? (
                          <button className="flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/20 transition-colors">
                            ■ {fmtDate(endRange.from)}–{fmtDate(endRange.to)}
                            <span className="ml-0.5 hover:text-primary/60" onClick={(e) => { e.stopPropagation(); setEndRange(undefined); }}>
                              <X className="h-2.5 w-2.5" />
                            </span>
                          </button>
                        ) : (
                          <button className="rounded p-0.5 hover:bg-muted transition-colors text-muted-foreground" title="Filter by end date">
                            <CalendarDays className="h-3 w-3 opacity-50" />
                          </button>
                        )}
                      </PopoverTrigger>
                      <PopoverContent align="start" className="w-auto p-0">
                        <div className="p-2 border-b flex items-center justify-between">
                          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">End date</p>
                          {endRange?.from && (
                            <button className="text-[10px] text-muted-foreground hover:text-foreground transition-colors" onClick={() => setEndRange(undefined)}>Clear</button>
                          )}
                        </div>
                        <div className="flex">
                          <Calendar mode="range" selected={endRange} onSelect={setEndRange} numberOfMonths={1} />
                          <div className="flex flex-col gap-0.5 border-l border-border p-2 w-[140px] shrink-0">
                            <p className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Quick pick</p>
                            {([
                              { label: 'Last 1 month',  range: () => { const t = new Date(); return { from: subMonths(t, 1), to: t }; } },
                              { label: 'Last 2 months', range: () => { const t = new Date(); return { from: subMonths(t, 2), to: t }; } },
                              { label: 'Last 3 months', range: () => { const t = new Date(); return { from: subMonths(t, 3), to: t }; } },
                              { label: 'This month',    range: () => { const t = new Date(); return { from: startOfMonth(t), to: endOfMonth(t) }; } },
                              { label: 'Next 1 month',  range: () => { const t = new Date(); return { from: t, to: addMonths(t, 1) }; } },
                              { label: 'Next 2 months', range: () => { const t = new Date(); return { from: t, to: addMonths(t, 2) }; } },
                            ] as const).map((p) => (
                              <button key={p.label} type="button" onClick={() => setEndRange(p.range())}
                                className="rounded-md px-2.5 py-1.5 text-left text-[12px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
                                {p.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="p-2 border-t flex justify-end">
                          <PopoverClose asChild><Button size="sm" variant="outline" className="h-7 text-xs">Done</Button></PopoverClose>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </span>
                </TableHead>

                <TableHead className="w-[90px] text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Trainers</TableHead>
                <TableHead className="w-20 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [1, 2, 3].map((i) => (
                  <TableRow key={i}>
                    {[50, 40, 30, 30, 15].map((w, j) => (
                      <TableCell key={j}><Skeleton className="h-4" style={{ width: `${w}%` }} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : visibleBatches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <EmptyState
                      icon={Layers}
                      title={`No ${activeTab} batches`}
                      description={activeTab === 'active' ? 'Create your first batch to get started.' : undefined}
                      className="py-12"
                    />
                  </TableCell>
                </TableRow>
              ) : (
                visibleBatches.map((b) => {
                  return (
                    <TableRow key={b.id}>
                      <TableCell className="font-medium">
                        <Link
                          to="/admin/batches/$batchId"
                          params={{ batchId: b.id }}
                          className="font-medium text-foreground hover:text-primary"
                        >
                          {b.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{b.org_name}</TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground text-sm">
                        {b.start_date ? formatDate(b.start_date) : '—'}
                        {(b.start_date || b.end_date) && ' – '}
                        {b.end_date ? formatDate(b.end_date) : b.start_date ? '…' : ''}
                      </TableCell>
                      <TableCell>
                        <TrainerAvatars trainers={b.trainers} onManage={() => setManageBatch(b)} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-0.5">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                aria-label="Curriculum"
                                onClick={() => navigate({ to: '/admin/batches/$batchId', params: { batchId: b.id } })}
                              >
                                <BookOpen className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Curriculum</TooltipContent>
                          </Tooltip>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="More actions">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setCloningBatch(b)}>
                                <Layers className="mr-2 h-4 w-4" /> Clone
                              </DropdownMenuItem>
                              {statusOf(b) === 'ended' && (
                                <DropdownMenuItem onClick={() => handleArchive(b)}>
                                  <Archive className="mr-2 h-4 w-4" /> Archive
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={async () => {
                                  setDeletingBatch(b);
                                  setDeletionPreview(null);
                                  setPreviewLoading(true);
                                  try {
                                    const preview = await adminApi.getBatchDeletionPreview(b.id);
                                    setDeletionPreview(preview);
                                  } catch {
                                    // non-fatal — dialog still opens without counts
                                  } finally {
                                    setPreviewLoading(false);
                                  }
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {showCreate && (
          <CreateBatchModal
            orgs={orgs}
            onClose={() => setShowCreate(false)}
            onCreated={() => {
              setShowCreate(false);
              loadBatches(orgFilter && orgFilter !== '__all__' ? orgFilter : undefined, activeTab);
            }}
          />
        )}
        {manageBatch && (
          <ManageTrainersDrawer
            batch={manageBatch}
            onClose={() => setManageBatch(null)}
            onChanged={() => loadBatches(orgFilter && orgFilter !== '__all__' ? orgFilter : undefined, activeTab)}
          />
        )}
        {cloningBatch && (
          <CloneBatchModal
            sourceBatch={cloningBatch}
            orgs={orgs}
            onClose={() => setCloningBatch(null)}
            onCloned={() => {
              setCloningBatch(null);
              loadBatches(orgFilter && orgFilter !== '__all__' ? orgFilter : undefined, activeTab);
            }}
          />
        )}

        <AlertDialog
          open={!!deletingBatch}
          onOpenChange={(open) => {
            if (!open) { setDeletingBatch(null); setDeletionPreview(null); }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete "{deletingBatch?.name}"?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>This will permanently delete the batch. This action cannot be undone.</p>
                  {previewLoading && <p className="text-xs">Loading impact…</p>}
                  {!previewLoading && deletionPreview && (
                    <ul className="list-disc list-inside space-y-0.5 text-xs">
                      {deletionPreview.student_count > 0 && (
                        <li>{deletionPreview.student_count} enrolled student{deletionPreview.student_count !== 1 ? 's' : ''} will be dropped</li>
                      )}
                      {deletionPreview.trainer_count > 0 && (
                        <li>{deletionPreview.trainer_count} active trainer assignment{deletionPreview.trainer_count !== 1 ? 's' : ''} will be ended</li>
                      )}
                      <li>All curriculum content will be archived</li>
                    </ul>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={deleteLoading}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleteLoading ? 'Deleting…' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TooltipProvider>
  );
}
